<template>
  <a-modal
    :visible="visible"
    :title="`Adjust Finetuning — Alert ${incidentId}`"
    :width="1200"
    :style="{ maxWidth: 'calc(100vw - 32px)' }"
    :footer="null"
    :bodyStyle="{ maxHeight: '70vh', overflowY: 'auto' }"
    @cancel="$emit('close')"
  >
    <div v-if="loading" class="d-flex justify-content-center align-items-center py-4">
      <a-icon type="loading" spin class="loading-icon" />
    </div>

    <template v-else>
      <a-alert
        type="info"
        show-icon
        class="mb-3"
        message="Preview of finetuning based on this alert"
      >
        <template slot="description">
          <div>
            The values detected in this alert are taken literally as the new thresholds, so an
            identical change will no longer trigger an alert. Review and adjust before applying.
          </div>
          <div class="mt-1">
            <strong>Why some do-not-monitor rules are unchecked:</strong>
            Broad content-level change assets such as Internal_CSS_#, In-line_JS_# or inline
            images are unchecked by default. Review the affected page’s code and finetune it with
            a specific rule instead. These rules can still be restored manually.
          </div>
          <div class="mt-1">
            <strong>Important:</strong>
            Finetune suggestions are generated automatically and should be treated as
            recommendations. Double-check every adjustment against the affected page and expected
            monitoring behavior before applying it.
          </div>
        </template>
      </a-alert>

      <a-tabs :activeKey="activeTabKey" @change="onTabChange">
        <a-tab-pane key="threshold" :tab="`Threshold (${thresholdRows.length})`" :forceRender="true">
          <template v-if="thresholdRows.length > 0">
            <div class="table-analytics adjust-finetune-table">
              <a-table
                :rowKey="record => record.element"
                :columns="ThresholdColumns"
                :data-source="thresholdRows"
                :pagination="false"
                :row-selection="thresholdRowSelection"
              >
                <template slot="newThreshold" slot-scope="text, record">
                  <a-input
                    type="text"
                    :value="String(getNewThresholdValue(record))"
                    :disabled="!selectedThresholdKeys.includes(record.element)"
                    @change="(e) => onNewThresholdChange(record, e.target.value)"
                  />
                </template>
              </a-table>
            </div>
            <div class="actions mt-3">
              <a-button
                type="primary"
                :loading="isApplyingThresholds"
                :disabled="selectedThresholdKeys.length === 0"
                @click="onApplyThresholds"
              >
                Apply {{ selectedThresholdKeys.length }} threshold(s)
              </a-button>
            </div>

            <RelatedThresholdApply
              v-if="selectedThresholdCount > 0 && (canApplyDomain || normalizedWebpageTags.length > 0)"
              class="mt-3"
              :domain="applyDomain"
              :tags="normalizedWebpageTags"
              :selected-tag-id="selectedBulkTagId"
              :applying="isApplyingThresholds"
              @update:selected-tag-id="selectedBulkTagId = $event"
              @apply-domain="onApplyFloorForDomain"
              @apply-tag="onApplyFloorForTag"
            />
          </template>
          <p v-else class="text-center text-muted py-4 mb-0">
            No content analytics changes with numeric values were detected in this alert.
          </p>
        </a-tab-pane>

        <a-tab-pane key="policy" :tab="`Do not monitor (${policyRuleCount})`" :forceRender="true">
          <PolicyGroupContent
            v-if="policyGroup && policyRuleCount > 0"
            :group="policyGroup"
            :groupIndex="0"
            :selectedRules="selectedPolicyRules"
            :editableRules="true"
            :rulesTableMinWidth="0"
            :ruleEdits="policyRuleEdits"
            :groupRuleEdits="policyGroupRuleEdits"
            :actionHref="policyActionHref"
            @toggle="onTogglePolicyRule(0, $event)"
            @change="onChangePolicyRule(0, $event)"
            @group-change="onChangeGroupedPolicyRule(0, $event)"
            @proceed="onProceedToPolicy"
          />
          <p v-else class="text-center text-muted py-4 mb-0">
            No new do-not-monitor rules were detected in this alert.
          </p>
        </a-tab-pane>
      </a-tabs>
    </template>
  </a-modal>
</template>

<script>
import PolicyGroupContent from '@shared/components/SuggestedPolicy/PolicyGroupContent.vue';
import RelatedThresholdApply from '@shared/components/RelatedThresholdApply.vue';
import WebpageService from '@modules/Webpages/services/webpage.service';
import PolicyService from '@modules/Policy/services/policy.service';
import policyRuleSelectionMixin from '@shared/mixins/policyRuleSelection.mixin';
import thresholdApplyMixin from '@shared/mixins/thresholdApply.mixin';
import { mapIntegrityElementTypeToPolicyRuleType } from '@constants/rules';
import {
  countGroupedRules,
  createRuleKey,
  dedupeRules,
  filterNewRules,
  isContentLevelChangeAssetRule,
  normalizeRules
} from '@shared/utils/policyRules';
import {
  countThresholdEntries,
  getFinetuneElementDescription,
  normalizeRelatedTags,
  parseThresholdInput,
  THRESHOLD_ELEMENT_TO_ATTRIBUTE
} from '@shared/utils/finetune';
import {
  generatePolicyPrefillHandle,
  removePolicyPrefill,
  storePolicyPrefill
} from '@shared/utils/policyPrefill';
import { extractHostname } from '@shared/utils/url';

// content_engine keys eligible for threshold apply; 'css' maps to 'link' on the API side.
const THRESHOLD_ELEMENT_KEYS = ['css', 'div', 'href', 'img', 'line_num', 'script', 'iframe', 'form'];

function buildThresholdColumns(comparisonTitle) {
  return [
    { title: 'No.', dataIndex: 'index', key: 'index', width: 50 },
    { title: 'Description', dataIndex: 'description', key: 'description', width: 200 },
    { title: comparisonTitle, dataIndex: 'comparisonValue', key: 'comparisonValue', width: 120 },
    { title: 'Current Threshold', dataIndex: 'currentThreshold', key: 'currentThreshold', width: 140 },
    { title: 'Alert Value', dataIndex: 'alertValue', key: 'alertValue', width: 110 },
    {
      title: 'New Threshold',
      dataIndex: 'newThreshold',
      key: 'newThreshold',
      width: 130,
      scopedSlots: { customRender: 'newThreshold' }
    }
  ];
}

export default {
  name: 'AdjustFinetuneModal',
  components: {
    PolicyGroupContent,
    RelatedThresholdApply
  },
  mixins: [policyRuleSelectionMixin, thresholdApplyMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    incidentId: {
      type: [String, Number],
      default: ''
    },
    webpageId: {
      type: [String, Number],
      default: null
    },
    webpageUrl: {
      type: String,
      default: ''
    },
    verifyResult: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      activeTabKey: 'threshold',
      loading: false,
      webpageThresholds: {},
      webpageTags: [],
      selectedBulkTagId: null,
      matchedPolicy: null,
      selectedThresholdKeys: [],
      newThresholdEdits: {},
      policyPrefillHandle: null
    };
  },
  computed: {
    isFromLastIncident() {
      return Boolean(this.verifyResult && this.verifyResult.changes_detected_from_last_incident);
    },
    changesDetected() {
      if (!this.verifyResult) return null;
      // Prefer the comparison against the last alert; fall back to the baseline comparison.
      return (
        this.verifyResult.changes_detected_from_last_incident ||
        this.verifyResult.changes_detected_from_baseline ||
        null
      );
    },
    applyDomain() {
      return extractHostname(this.webpageUrl) || '';
    },
    canApplyDomain() {
      return this.applyDomain.length >= 4;
    },
    normalizedWebpageTags() {
      return normalizeRelatedTags(this.webpageTags);
    },
    selectedThresholdCount() {
      return Object.keys(this.buildSelectedThresholdsMap()).length;
    },
    ThresholdColumns() {
      return buildThresholdColumns(this.isFromLastIncident ? 'Last Alert Value' : 'Baseline Value');
    },
    thresholdRows() {
      const contentEngine = (this.changesDetected && this.changesDetected.content_engine) || {};
      const rows = [];
      THRESHOLD_ELEMENT_KEYS.forEach((key) => {
        const value = contentEngine[key];
        if (!value) return;
        const difference = Number(value.difference);
        if (!Number.isFinite(difference)) return;
        // Thresholds are absolute magnitudes; the detected difference may be negative
        // (e.g. fewer lines/elements than the comparison point).
        const alertValue = Math.abs(difference);
        const element = key === 'css' ? 'link' : key;
        const currentThreshold = this.webpageThresholds[`threshold_${element}`];
        const comparisonValue = this.isFromLastIncident ? value.last_incident : value.baseline;
        rows.push({
          index: rows.length + 1,
          element,
          description: getFinetuneElementDescription(element),
          comparisonValue: comparisonValue != null ? comparisonValue : '—',
          currentThreshold: currentThreshold != null ? currentThreshold : '—',
          alertValue
        });
      });
      return rows;
    },
    thresholdRowSelection() {
      return {
        selectedRowKeys: this.selectedThresholdKeys,
        onChange: (keys) => {
          this.selectedThresholdKeys = keys;
        }
      };
    },
    policyGroup() {
      const rules = this.buildPolicyRules();
      if (rules.length === 0) return null;

      const urlMeta = {
        id: this.webpageId,
        url: this.webpageUrl || `URL ID: ${this.webpageId}`
      };

      if (this.matchedPolicy) {
        const existingRuleKeys = new Set(
          (this.matchedPolicy.rules || []).map((rule) => createRuleKey(rule))
        );
        return {
          mode: 'existing',
          policyId: this.matchedPolicy.id,
          policyName: this.matchedPolicy.name || '',
          urls: [urlMeta],
          urlIds: [this.webpageId],
          rules,
          newRulesOnly: rules.filter((rule) => !existingRuleKeys.has(createRuleKey(rule)))
        };
      }

      return {
        mode: 'new',
        urls: [urlMeta],
        urlIds: [this.webpageId],
        rules
      };
    },
    policyPreviewRules() {
      if (!this.policyGroup) return [];
      return this.policyGroup.mode === 'existing'
        ? this.policyGroup.newRulesOnly || []
        : this.policyGroup.rules || [];
    },
    policyRuleCount() {
      return countGroupedRules(this.policyPreviewRules);
    },
    policyActionHref() {
      if (!this.policyGroup) return '#';
      const base =
        this.policyGroup.mode === 'existing' ? `/policy/${this.policyGroup.policyId}` : '/policy/add';
      if (!this.policyPrefillHandle) return base;
      return `${base}?prefill=${encodeURIComponent(this.policyPrefillHandle)}`;
    }
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.activeTabKey = 'threshold';
        this.newThresholdEdits = {};
        this.policyPrefillHandle = generatePolicyPrefillHandle();
        this.loadData();
      } else {
        this.cleanupStagedPrefill();
      }
    }
  },
  beforeDestroy() {
    this.cleanupStagedPrefill();
  },
  methods: {
    onTabChange(activeKey) {
      this.activeTabKey = activeKey;
    },
    async loadData() {
      this.loading = true;
      this.webpageThresholds = {};
      this.webpageTags = [];
      this.selectedBulkTagId = null;
      this.matchedPolicy = null;
      try {
        const [webpage, policies] = await Promise.all([
          WebpageService.get(this.webpageId),
          PolicyService.getList().catch(() => [])
        ]);
        this.webpageThresholds = (webpage && webpage.threshold) || {};
        this.webpageTags = Array.isArray(webpage && webpage.tags) ? webpage.tags : [];
        const policyList = Array.isArray(policies) ? policies : [];
        this.matchedPolicy =
          policyList.find((policy) =>
            (policy.urls || []).some((urlItem) => String(urlItem.id) === String(this.webpageId))
          ) || null;
      } catch {
        this.$showErrorMessage('Unable to load webpage configuration for finetuning.');
      } finally {
        this.loading = false;
        this.initializeSelection();
        this.stagePolicyPrefill();
      }
    },
    initializeSelection() {
      this.selectedThresholdKeys = this.thresholdRows.map((row) => row.element);
      // Same default as the auto finetune suggestion screen: broad content-level change
      // assets (Internal_CSS_#, In-line_JS_#, ...) start unchecked and must be opted into.
      this.resetPolicyRuleSelection(1, (rule) => !isContentLevelChangeAssetRule(rule));
      const tags = this.normalizedWebpageTags;
      this.selectedBulkTagId = tags.length > 1 ? tags[0].id : null;
    },
    getNewThresholdValue(record) {
      return this.newThresholdEdits[record.element] != null
        ? this.newThresholdEdits[record.element]
        : record.alertValue;
    },
    onNewThresholdChange(record, value) {
      this.$set(this.newThresholdEdits, record.element, value);
    },
    /**
     * Shared thresholds map for single-URL and bulk apply: { threshold_*: number }.
     */
    buildSelectedThresholdsMap() {
      const thresholds = {};
      this.thresholdRows.forEach((row) => {
        if (!this.selectedThresholdKeys.includes(row.element)) return;
        const attr = THRESHOLD_ELEMENT_TO_ATTRIBUTE[row.element];
        if (!attr) return;
        const value = parseThresholdInput(this.getNewThresholdValue(row));
        if (value == null) return;
        thresholds[attr] = Math.abs(value);
      });
      return thresholds;
    },
    onApplyThresholds() {
      const thresholds = this.buildSelectedThresholdsMap();
      const urlsPayload =
        Object.keys(thresholds).length === 0
          ? []
          : [{ url_id: this.webpageId, thresholds }];
      const count = countThresholdEntries(urlsPayload);
      if (!count) {
        this.$message.warning(
          'No valid numeric thresholds to apply. Check that selected rows have valid numbers.'
        );
        return;
      }
      this.$confirm({
        title: `Apply ${count} threshold(s) from this alert?`,
        okText: 'Apply',
        cancelText: 'Cancel',
        onOk: () => this.runApplyThresholds(urlsPayload)
      });
    },
    onApplyFloorForDomain() {
      const thresholds = this.buildSelectedThresholdsMap();
      const count = Object.keys(thresholds).length;
      const domain = this.applyDomain;
      if (!count || !this.canApplyDomain) {
        this.$message.warning(
          'No valid numeric thresholds to apply, or domain is missing / too short.'
        );
        return;
      }
      this.$confirm({
        title: `Apply ${count} minimum threshold(s) to all URLs matching domain ${domain}? Existing higher values will remain unchanged.`,
        okText: 'Apply',
        cancelText: 'Cancel',
        onOk: () => this.runApplyThresholdsByTarget({ domain }, thresholds)
      });
    },
    onApplyFloorForTag(tagId) {
      const thresholds = this.buildSelectedThresholdsMap();
      const count = Object.keys(thresholds).length;
      if (!count || tagId == null) {
        this.$message.warning(
          'No valid numeric thresholds to apply. Check that selected rows have valid numbers.'
        );
        return;
      }
      const tag = this.normalizedWebpageTags.find((t) => String(t.id) === String(tagId));
      const tagLabel = (tag && tag.name) || `tag ${tagId}`;
      this.$confirm({
        title: `Apply ${count} minimum threshold(s) to all URLs matching tag ${tagLabel}? Existing higher values will remain unchanged.`,
        okText: 'Apply',
        cancelText: 'Cancel',
        onOk: () =>
          this.runApplyThresholdsByTarget({ tag_id: Number(tagId) || tagId }, thresholds)
      });
    },
    buildPolicyRules() {
      const integrityEngine = (this.changesDetected && this.changesDetected.integrity_engine) || {};
      const rawRules = [];
      Object.entries(integrityEngine).forEach(([elementType, changes]) => {
        if (!Array.isArray(changes)) return;
        const policyRuleType = mapIntegrityElementTypeToPolicyRuleType(elementType);
        if (!policyRuleType) return;
        changes.forEach((change) => {
          const field = String(change || '').trim();
          if (!field) return;
          rawRules.push({
            type: policyRuleType,
            condition: 'Contains',
            field,
            action: 'Do not monitor',
            occurrences: [{ url: this.webpageUrl || `URL ID: ${this.webpageId}`, snippet: '' }]
          });
        });
      });
      return dedupeRules(rawRules);
    },
    getPolicySelectionRules(groupIndex) {
      return groupIndex === 0 ? this.policyPreviewRules : [];
    },
    onPolicyRuleSelectionChanged() {
      this.stagePolicyPrefill();
    },
    buildPolicyPrefillPayload() {
      const selectedRules = this.getSelectedPolicyRules(0);
      if (!this.policyGroup || !selectedRules.length) return null;

      const normalizedRules = normalizeRules(
        this.policyGroup.mode === 'existing'
          ? filterNewRules(selectedRules, this.matchedPolicy.rules || [])
          : selectedRules
      );
      if (!normalizedRules.length) return null;

      if (this.policyGroup.mode === 'existing') {
        return {
          mode: 'edit',
          policyId: String(this.policyGroup.policyId),
          rules: normalizedRules
        };
      }
      return {
        mode: 'new',
        url_ids: [String(this.webpageId)],
        rules: normalizedRules
      };
    },
    stagePolicyPrefill() {
      if (!this.policyPrefillHandle) return;
      const payload = this.buildPolicyPrefillPayload();
      try {
        if (payload) {
          storePolicyPrefill(this.policyPrefillHandle, payload);
        } else {
          removePolicyPrefill(this.policyPrefillHandle);
        }
      } catch (_) { /* ignore */ }
    },
    cleanupStagedPrefill() {
      if (!this.policyPrefillHandle) return;
      const handle = this.policyPrefillHandle;
      this.policyPrefillHandle = null;
      if (this._prefillInFlight) {
        // The destination route consumes and removes the payload; sweep later as a safety net.
        this._prefillInFlight = false;
        setTimeout(() => {
          removePolicyPrefill(handle);
        }, 5000);
        return;
      }
      removePolicyPrefill(handle);
    },
    onProceedToPolicy(options = {}) {
      this.stagePolicyPrefill();
      const payload = this.buildPolicyPrefillPayload();
      if (!payload) return;

      if (options && options.openInNewTab) {
        // The anchor href already carries ?prefill=<handle>; the browser opens the new tab.
        return;
      }

      this._prefillInFlight = true;
      const href = this.policyActionHref;
      this.$emit('close');
      this.$router.push(href);
    }
  }
};
</script>

<style lang="scss" scoped>
.loading-icon {
  font-size: 28px;
  color: #04b0ed;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.adjust-finetune-table {
  overflow-x: auto;
}

.table-analytics {
  ::v-deep .ant-table-thead {
    font-weight: 500;

    tr th {
      background: #fff;
    }
  }
}
</style>
